/* eslint-disable no-unused-vars */
import { api } from "../../../api";

// Set to true only when a clearly labelled frontend demo is needed.
const RCA_DEMO_MODE = false;

const MOCK_CAUSAL_CHAINS = [
  "Traffic spike -> connections not released -> pool exhausted -> 5xx errors",
  "Deploy -> unbounded cache growth -> memory pressure -> OOM restart loop",
  "Release deployed -> latency regression -> error budget burn -> alerts",
  "Cache eviction -> mass misses -> DB overload -> cascading latency",
  "Log surge -> disk fills -> writes fail -> service degraded",
];

const MOCK_FAILURE_POINTS = [
  "Connection pool (max_connections reached)",
  "Service heap (OOMKilled by kubelet)",
  "Application code path (new release)",
  "Cache tier (eviction under load)",
  "Node disk (>95% utilisation)",
  "Edge TLS termination",
  "Service discovery / DNS",
  "API rate limiter (429 threshold)",
  "Runtime configuration (drift from baseline)",
  "Queue consumers (lag growing)",
];

const MOCK_SERVICES = [
  "payments",
  "auth",
  "checkout",
  "notifications",
  "search",
  "user-profile",
  "ledger",
  "reporting",
  "ingestion",
  "gateway",
];

const MOCK_USER_ACTIVITY = [
  "Normal",
  "Traffic spike (+180%)",
  "Bulk import job running",
  "Scheduled batch window",
  "Off-peak / low traffic",
];

const MOCK_VALIDATION = ["Validated", "Auto-validated", "Pending review", "Rejected"];

const MOCK_LEARNING = [
  "Pattern added to detection model",
  "Runbook updated with additional diagnostic step",
  "Matched known pattern — no new learning",
  "Alert threshold tuned to reduce recurrence",
  "Correlation signature added to engine",
];

function hashString(value) {
  let hash = 2166136261;
  const text = String(value);
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function seededRandom(seed) {
  let state = seed >>> 0;
  return function next() {
    state += 0x6d2b79f5;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pickFrom(list, rand) {
  return list[Math.floor(rand() * list.length)];
}

function intBetween(rand, min, max) {
  return Math.floor(rand() * (max - min + 1)) + min;
}

function stripMarkdown(text) {
  if (!text) return "";
  return String(text)
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/[#*`_>]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function firstSentence(text, maxLength = 110) {
  const clean = stripMarkdown(text);
  if (!clean) return "";
  const match = clean.match(/^.*?[.!?](?=\s|$)/);
  const sentence = match ? match[0] : clean;
  return sentence.length > maxLength ? `${sentence.slice(0, maxLength - 1)}…` : sentence;
}

function summariseSuggestion(suggestions) {
  if (!Array.isArray(suggestions) || suggestions.length === 0) return "";
  const first = suggestions[0];
  const body =
    first.suggestion ||
    first.content ||
    first.description ||
    first.summary ||
    first.text ||
    "";
  const summary = firstSentence(body);
  if (summary) return summary;
  return first.suggestion_type ? first.suggestion_type.replace(/_/g, " ") : "";
}

function resolveInfrastructureDependency(value) {
  if (!value) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    const name = value.name || value.hostname || value.server_name || value.label;
    return name || "—";
  }
  return "—";
}

function buildRecord(item = {}) {
  const confidence = typeof item.confidence_pct === "number" ? item.confidence_pct : null;
  const escalated = typeof item.escalated === "boolean" ? item.escalated : false;
  const hasHistoricalMatches =
    typeof item.historical_matches_count === "number" ? item.historical_matches_count > 0 : false;

  const validationStatus =
    typeof item.validation_status === "string" && item.validation_status
      ? item.validation_status
      : "";

  const suggestedFix =
    typeof item.suggested_fix === "string" && item.suggested_fix
      ? item.suggested_fix
      : summariseSuggestion(item?.rca?.suggestions) || "";

  const probableFailurePoint =
    typeof item.probable_failure_point === "string" && item.probable_failure_point
      ? item.probable_failure_point
      : "";

  const historicalSimilarityPct =
    typeof item.historical_similarity_pct === "number"
      ? item.historical_similarity_pct
      : hasHistoricalMatches
        ? null
        : null;

  const automatedResolutionAvailable =
    typeof item.automated_resolution_available === "string" && item.automated_resolution_available
      ? item.automated_resolution_available
      : typeof item.automated_resolution_available === "boolean"
        ? item.automated_resolution_available
          ? "Yes"
          : "No"
        : "";

  return {
    rca_id: item.rca_id ?? item.id ?? null,
    ticket_id: item.ticket_id ?? null,
    incident_id: item.incident_id ?? null,
    detection_timestamp: item.detection_timestamp ?? null,
    ticket_title: item.ticket_title ?? item.title ?? "",
    ai_root_cause: item.ai_root_cause ?? "",
    confidence_pct: confidence,
    causal_chain: item.causal_chain ?? "",
    correlated_events: typeof item.correlated_events === "number" ? item.correlated_events : 0,
    correlated_logs:
      typeof item.correlated_logs_count === "number"
        ? item.correlated_logs_count
        : 0,
    correlated_metrics:
      typeof item.correlated_metrics_count === "number"
        ? item.correlated_metrics_count
        : 0,
    correlated_traces: typeof item.correlated_traces === "number" ? item.correlated_traces : 0,
    infrastructure_dependency: resolveInfrastructureDependency(item.infrastructure_dependency),
    service_dependency: item.service_dependency ?? "",
    configuration_changes: item.configuration_changes ?? "",
    deployment_changes: item.deployment_changes ?? "",
    recent_patches: item.recent_patches ?? "",
    network_changes: item.network_changes ?? "",
    user_activity: item.user_activity ?? "",
    probable_failure_point: probableFailurePoint,
    historical_similarity_pct: historicalSimilarityPct,
    suggested_fix: suggestedFix,
    automated_resolution_available: automatedResolutionAvailable,
    validation_status: validationStatus,
    validation_at: item.validation_at ?? null,
    validated_by: item.validated_by ?? null,
    learning_outcome: item.learning_outcome ?? "",
    status: item.status ?? item.rca?.status ?? null,
    rca_status: item.status ?? item.rca?.status ?? null,
    escalated,
    rca: item.rca ?? null,
    is_demo: item.is_demo === true,
  };
}

function buildQueryString({ page = 1, pageSize = 15, filters = {} } = {}) {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });

  if (filters.search) params.set("search", filters.search);
  if (filters.rootCause) params.set("root_cause", filters.rootCause);
  if (filters.validation) params.set("validation_status", filters.validation);

  return params.toString();
}

let reportPayloadCache = new Map();
let reportPayloadInflight = new Map();

async function requestRcaReports({ page = 1, pageSize = 15, filters = {} } = {}) {
  const query = buildQueryString({ page, pageSize, filters });

  if (reportPayloadCache.has(query)) {
    return reportPayloadCache.get(query);
  }

  if (reportPayloadInflight.has(query)) {
    return reportPayloadInflight.get(query);
  }

  console.info("RCA reports request", query);
  const requestPromise = api(`/rca-reports?${query}`);
  reportPayloadInflight.set(query, requestPromise);

  try {
    const payload = await requestPromise;
    console.info("RCA reports response", {
      total: payload?.total,
      itemCount: payload?.items?.length,
      summary: payload?.summary,
      analyticsKeys: Object.keys(payload?.analytics || {}),
      filterOptionKeys: Object.keys(payload?.filter_options || payload?.filterOptions || {}),
    });
    reportPayloadCache.set(query, payload);
    console.log("RCA reports payload", payload);
    return payload;
  } catch (error) {
    console.error("RCA reports request failed", error);
    throw error;
  } finally {
    reportPayloadInflight.delete(query);
  }
}

function normalizeRecords(payload) {
  const rawItems = Array.isArray(payload?.items) ? payload.items : [];
  return rawItems.map((item) => buildRecord(item));
}

function normalizeSummary(summary = {}) {
  return {
    total: summary.total ?? 0,
    avg_confidence: summary.avg_confidence ?? 0,
    auto_resolvable: summary.auto_resolvable ?? 0,
    auto_resolvable_pct: summary.auto_resolvable_pct ?? 0,
    validated: summary.validated ?? 0,
    rejected: summary.rejected ?? 0,
  };
}

function normalizeAnalytics(analytics = {}) {
  return {
    rootCause: Array.isArray(analytics.rootCause) && analytics.rootCause.length > 0
      ? analytics.rootCause
      : [],
    issueTypes: Array.isArray(analytics.issue_breakdown) && analytics.issue_breakdown.length > 0
      ? analytics.issue_breakdown
      : [],
    confidence: Array.isArray(analytics.confidence) && analytics.confidence.length > 0
      ? analytics.confidence
      : [],
    overTime: Array.isArray(analytics.overTime) && analytics.overTime.length > 0
      ? analytics.overTime
      : [],
    validation: Array.isArray(analytics.validation) && analytics.validation.length > 0
      ? analytics.validation
      : [],
  };
}

function normalizeFilterOptions(filterOptions = {}) {
  return {
    issueTypes: [...new Set(filterOptions.issue_breakdown.map((record) => record.issue_type))].sort(),
    validations: [...new Set(filterOptions.validations.map((record) => record.validation_status))].sort(),
  };
}

function applyFilters(records, filters = {}) {
  return records.filter((record) => {
    if (filters.search) {
      const query = filters.search.toLowerCase();
      const haystack = `${record.rca_id} ${record.incident_id} ${record.ai_root_cause} ${
        record.issue_type || ""
      }`.toLowerCase();
      if (!haystack.includes(query)) return false;
    }
    if (filters.issueType && record.issue_type !== filters.issueType) return false;
    if (filters.validation && record.validation_status !== filters.validation) return false;
    if (
      filters.autoResolution &&
      record.automated_resolution_available !== filters.autoResolution
    ) {
      return false;
    }
    return true;
  });
}

function createDemoRecords() {
  return [
    {
      rca_id: "demo-rca-001",
      ticket_id: "demo-ticket-001",
      ticket_title: "Demo RCA report",
      ai_root_cause: "Demo root cause",
      confidence_pct: 85,
      detection_timestamp: new Date().toISOString(),
      validation_status: "Pending review",
      status: "demo",
      is_demo: true,
    },
  ].map(buildRecord);
}

export function invalidateRcaCache() {
  reportPayloadCache.clear();
  reportPayloadInflight.clear();
}

export async function getRcaFilterOptions() {
  const payload = await requestRcaReports({ page: 1, pageSize: 100, filters: {} });
  console.log("RCA filter options payload", payload?.filter_options || payload?.filterOptions || {});
  return normalizeFilterOptions(payload?.filter_options || payload?.filterOptions || {});
}

export async function fetchRcaStats() {
  const payload = await requestRcaReports({ page: 1, pageSize: 100, filters: {} });
  return normalizeSummary(payload?.summary || {});
}

export async function fetchRcaCharts() {
  const payload = await requestRcaReports({ page: 1, pageSize: 100, filters: {} });
  return normalizeAnalytics(payload?.analytics || {});
}

export async function fetchRcaRecords({ page = 1, pageSize = 15, filters = {} } = {}) {
  const payload = await requestRcaReports({ page, pageSize, filters });
  const backendRecords = normalizeRecords(payload);
  const records =
    RCA_DEMO_MODE && backendRecords.length === 0 ? createDemoRecords() : backendRecords;
  const filtered = applyFilters(records, filters);

  return {
    items: filtered,
    total: payload?.total ?? 0,
  };
}
